import { useEffect, useState } from 'react'
import { Plus, RefreshCw, ArrowUp, ArrowDown, X, Copy, Check } from 'lucide-react'
import { devicesAPI } from '../api/client'
import { useOrg } from '../contexts/OrgContext'

function DevicesPage() {
  const { currentOrg } = useOrg()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    type: 'rfid_terminal',
    hardware_model: '',
    location: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // API key display state
  const [newDeviceApiKey, setNewDeviceApiKey] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (currentOrg?.org_id) {
      loadDevices()
    }
  }, [currentOrg?.org_id])

  async function loadDevices() {
    if (!currentOrg?.org_id) return

    try {
      setLoading(true)
      setError('')
      const response = await devicesAPI.getAll({ org_id: currentOrg.org_id })
      setDevices(response.data?.devices || [])
    } catch (err) {
      setError(err.message || 'Failed to load devices')
      console.error('Devices error:', err)
    } finally {
      setLoading(false)
    }
  }

  function formatTimestamp(timestamp) {
    if (!timestamp) return 'Never'
    return new Date(timestamp).toLocaleString()
  }

  function handleOpenAddModal() {
    setShowAddModal(true)
    setFormData({
      name: '',
      type: 'rfid_terminal',
      hardware_model: '',
      location: ''
    })
    setSubmitError('')
    setNewDeviceApiKey('')
    setCopied(false)
  }

  function handleCloseModal() {
    setShowAddModal(false)
    setNewDeviceApiKey('')
    setCopied(false)
  }

  function handleInputChange(e) {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!formData.name.trim()) {
      setSubmitError('Device name is required')
      return
    }

    try {
      setSubmitting(true)
      setSubmitError('')

      const response = await devicesAPI.create({
        ...formData,
        org_id: currentOrg.org_id
      })

      // Store the API key
      setNewDeviceApiKey(response.data.api_key)

      // Reload devices list
      await loadDevices()

      // Don't close modal yet - show API key first
    } catch (err) {
      setSubmitError(err.message || 'Failed to create device')
      console.error('Create device error:', err)
    } finally {
      setSubmitting(false)
    }
  }

  function handleCopyApiKey() {
    navigator.clipboard.writeText(newDeviceApiKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Sort devices if sort column is set
  const sortedDevices = sortColumn ? [...devices].sort((a, b) => {
    let aVal, bVal

    switch (sortColumn) {
      case 'device_id':
        aVal = a.device_id || ''
        bVal = b.device_id || ''
        break
      case 'name':
        aVal = a.name || ''
        bVal = b.name || ''
        break
      case 'type':
        aVal = a.type || ''
        bVal = b.type || ''
        break
      case 'location':
        aVal = a.location || ''
        bVal = b.location || ''
        break
      case 'status':
        aVal = a.status || ''
        bVal = b.status || ''
        break
      case 'last_seen':
        aVal = a.last_seen ? new Date(a.last_seen).getTime() : 0
        bVal = b.last_seen ? new Date(b.last_seen).getTime() : 0
        break
      case 'firmware_version':
        aVal = a.firmware_version || ''
        bVal = b.firmware_version || ''
        break
      default:
        return 0
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  }) : devices

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Devices <span style={{ fontSize: '1.25rem', fontWeight: '500', color: 'var(--text-light)' }}>({devices.length})</span></h1>
          <p>Manage RFID terminals and access points</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-secondary" onClick={loadDevices}>
            <RefreshCw size={18} />
            Refresh
          </button>
          <button className="btn btn-primary" onClick={handleOpenAddModal}>
            <Plus size={18} />
            Add Device
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        {sortedDevices.length === 0 ? (
          <p className="no-data">No devices found</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('device_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Device ID
                      {sortColumn === 'device_id' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Name
                      {sortColumn === 'name' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('type')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Type
                      {sortColumn === 'type' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('location')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Location
                      {sortColumn === 'location' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Status
                      {sortColumn === 'status' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('last_seen')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Last Seen
                      {sortColumn === 'last_seen' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('firmware_version')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Firmware
                      {sortColumn === 'firmware_version' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedDevices.map((device) => (
                  <tr key={device.device_id}>
                    <td>{device.device_id}</td>
                    <td>{device.name}</td>
                    <td>
                      <span className="badge badge-info">
                        {device.type || 'rfid_terminal'}
                      </span>
                    </td>
                    <td>{device.location || '-'}</td>
                    <td>
                      <span className={`badge ${
                        device.status === 'online' ? 'badge-success' :
                        device.status === 'offline' ? 'badge-danger' :
                        'badge-warning'
                      }`}>
                        {device.status}
                      </span>
                    </td>
                    <td>{formatTimestamp(device.last_seen)}</td>
                    <td>{device.firmware_version || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Device Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{newDeviceApiKey ? 'Device Created!' : 'Add New Device'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>

            {!newDeviceApiKey ? (
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {submitError && (
                    <div className="alert alert-danger">{submitError}</div>
                  )}

                  <div className="form-group">
                    <label>Device Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Front Door Scanner"
                      required
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label>Device Type *</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="rfid_terminal">RFID Terminal (Desktop)</option>
                      <option value="door_controller">Door Controller</option>
                      <option value="mobile_app">Mobile App</option>
                      <option value="api_integration">API Integration</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Hardware Model</label>
                    <input
                      type="text"
                      name="hardware_model"
                      value={formData.hardware_model}
                      onChange={handleInputChange}
                      placeholder="e.g., JAGTEK Door Access Terminal (DAT-32-C6)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g., Main Entrance, Front Desk"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? 'Creating...' : 'Create Device'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="modal-body">
                  <div className="alert alert-success">
                    Device created successfully!
                  </div>

                  <div className="alert alert-warning" style={{ marginTop: '1rem' }}>
                    <strong>⚠️ Important:</strong> Save this API key! It will not be shown again.
                  </div>

                  <div className="form-group">
                    <label>API Key</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        value={newDeviceApiKey}
                        readOnly
                        style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCopyApiKey}
                        style={{ minWidth: '100px' }}
                      >
                        {copied ? (
                          <>
                            <Check size={18} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={18} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-light)' }}>
                      Use this API key in the device configuration (X-API-Key header)
                    </small>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCloseModal}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DevicesPage
